import { useState, useEffect, useRef } from 'react';
import { Menu, X, Phone, Instagram, Send, MapPin, Users, Building2, Globe, ArrowRight } from 'lucide-react';
import en, { type Lang, type Translation } from './translations';
import './App.css';

function RussiaFlag({ size = 36 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
      <clipPath id="ru-clip">
        <circle cx="18" cy="18" r="18"/>
      </clipPath>
      <g clipPath="url(#ru-clip)">
        <rect width="36" height="12" fill="#FFFFFF"/>
        <rect y="12" width="36" height="12" fill="#0039A6"/>
        <rect y="24" width="36" height="12" fill="#D52B1E"/>
      </g>
    </svg>
  );
}

function UKFlag({ size = 36 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
      <clipPath id="uk-clip">
        <circle cx="18" cy="18" r="18"/>
      </clipPath>
      <g clipPath="url(#uk-clip)">
        <rect width="36" height="36" fill="#012169"/>
        <line x1="0" y1="0" x2="36" y2="36" stroke="#FFFFFF" strokeWidth="7"/>
        <line x1="36" y1="0" x2="0" y2="36" stroke="#FFFFFF" strokeWidth="7"/>
        <line x1="0" y1="0" x2="14" y2="18" stroke="#C8102E" strokeWidth="4.5"/>
        <line x1="22" y1="18" x2="36" y2="36" stroke="#C8102E" strokeWidth="4.5"/>
        <line x1="36" y1="0" x2="22" y2="18" stroke="#C8102E" strokeWidth="4.5"/>
        <line x1="14" y1="18" x2="0" y2="36" stroke="#C8102E" strokeWidth="4.5"/>
        <rect x="13.5" y="0" width="9" height="36" fill="#FFFFFF"/>
        <rect x="0" y="13.5" width="36" height="9" fill="#FFFFFF"/>
        <rect x="15" y="0" width="6" height="36" fill="#C8102E"/>
        <rect x="0" y="15" width="36" height="6" fill="#C8102E"/>
      </g>
    </svg>
  );
}

function WhatsAppIcon({ className, size = 24 }: { className?: string; size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.123 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
      />
    </svg>
  );
}

function TelegramIcon({ className, size = 24 }: { className?: string; size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"
      />
    </svg>
  );
}

function TikTokModal({ id, onClose }: { id: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm mx-4 rounded-2xl overflow-hidden shadow-2xl"
        style={{ aspectRatio: '9/16' }}
        onClick={e => e.stopPropagation()}
      >
        <iframe
          className="w-full h-full"
          src={`https://www.tiktok.com/embed/v2/${id}?autoplay=1&loop=1`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors z-10"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

function TikTokCard({ id, username, onPlay, watchLabel = 'Watch Video' }: { id: string; username: string; onPlay: () => void; watchLabel?: string }) {
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      const url = `https://www.tiktok.com/oembed?url=https://www.tiktok.com/@${username}/video/${id}`;
      fetch(url).then(r => r.json()).then(data => setThumbnail(data.thumbnail_url)).catch(() => {});
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [id, username]);

  return (
    <button
      ref={ref}
      onClick={onPlay}
      className="group relative w-full rounded-2xl overflow-hidden shadow-lg bg-[#111] block cursor-pointer"
      style={{ aspectRatio: '9/16' }}
    >
      {thumbnail && (
        <img
          src={thumbnail}
          alt="TikTok video"
          className="w-full h-full object-cover"
          loading="lazy"
        />
      )}
      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
        <span className="text-white text-xs font-sans tracking-wide opacity-80">{watchLabel}</span>
      </div>
      <div className="absolute top-3 right-3">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
        </svg>
      </div>
    </button>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [activeTikTok, setActiveTikTok] = useState<string | null>(null);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [formState, setFormState] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [heroFormData, setHeroFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [heroFormState, setHeroFormState] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [lang, setLang] = useState<Lang>(() => {
    const s = localStorage.getItem('kg_lang');
    return s === 'ru' ? 'ru' : 'en';
  });
  const [t, setT] = useState<Translation>(en);
  const lenisRef = useRef<any>(null);

  useEffect(() => {
    localStorage.setItem('kg_lang', lang);
    if (lang === 'en') { setT(en); return; }
    import(`./translations/${lang}.ts`).then(m => setT(m.default));
  }, [lang]);

  useEffect(() => {
    document.getElementById('hero-splash')?.remove();
  }, []);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      import('lenis'),
      import('gsap'),
      import('gsap/ScrollTrigger'),
    ]).then(([{ default: Lenis }, { gsap }, { ScrollTrigger }]) => {
      if (cancelled) return;

      gsap.registerPlugin(ScrollTrigger);

      const lenis = new Lenis({ lerp: 0.08, smoothWheel: true });
      lenisRef.current = lenis;

      lenis.on('scroll', ScrollTrigger.update);

      const raf = (time: number) => { lenis.raf(time); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);

      gsap.utils.toArray<HTMLElement>('.reveal').forEach((el) => {
        gsap.fromTo(el,
          { opacity: 0, y: 40 },
          {
            opacity: 1, y: 0, duration: 0.8, ease: 'power2.out',
            scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' },
          }
        );
      });

      gsap.utils.toArray<HTMLElement>('.reveal-stagger').forEach((container) => {
        const children = container.children;
        gsap.fromTo(children,
          { opacity: 0, y: 50 },
          {
            opacity: 1, y: 0, duration: 0.7, ease: 'power2.out', stagger: 0.12,
            scrollTrigger: { trigger: container, start: 'top 85%', toggleActions: 'play none none none' },
          }
        );
      });

      lenisRef.current._gsap = { lenis, ScrollTrigger };
    });

    return () => {
      cancelled = true;
      if (lenisRef.current?._gsap) {
        lenisRef.current._gsap.lenis.destroy();
        lenisRef.current._gsap.ScrollTrigger.killAll();
      }
    };
  }, []);

  const scrollToSection = (id: string) => {
    setMenuOpen(false);
    const element = document.getElementById(id);
    if (element && lenisRef.current) {
      lenisRef.current.scrollTo(element, { offset: -80, duration: 1.4, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    }
  };

  const navLinks = [
    { label: t.nav.home, id: 'hero' },
    { label: t.nav.services, id: 'services' },
    { label: t.nav.destinations, id: 'destinations' },
    { label: t.nav.about, id: 'about' },
  ];

  return (
    <div className="min-h-screen bg-parchment">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-transparent md:bg-midnight/90 md:backdrop-blur-sm md:border-b md:border-gold/20">
        <div
          className="flex items-center justify-between px-4 sm:px-6 lg:px-12 py-6"
          style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
        >
          {/* Logo */}
          <button 
            onClick={() => scrollToSection('hero')}
            className="flex items-center gap-3"
          >
            <img
              src="/logo.webp"
              alt="Kingdom Group"
              width="957"
              height="522"
              className="h-[3rem] w-auto object-contain"
            />
          </button>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              if (link.id === 'services') {
                return (
                  <div key={link.id} className="relative group">
                    <button
                      onClick={() => scrollToSection('services')}
                      className="font-sans text-sm text-white hover:text-gold transition-colors flex items-center gap-1"
                    >
                      {link.label}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-200 group-hover:rotate-180"><path d="M6 9l6 6 6-6"/></svg>
                    </button>
                    <div className="absolute top-full left-0 pt-3 hidden group-hover:block z-50">
                      <div className="bg-[#0a1710] border border-gold/20 rounded-sm shadow-2xl min-w-[230px] py-1.5">
                        <a href="/umrah-guide" className="flex items-center gap-2.5 px-4 py-2.5 font-sans text-sm text-white/85 hover:text-gold hover:bg-white/5 transition-colors">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gold/60 flex-shrink-0"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          Umrah Guide
                        </a>
                        <a href="/makkah-madinah-private-tours" className="flex items-center gap-2.5 px-4 py-2.5 font-sans text-sm text-white/85 hover:text-gold hover:bg-white/5 transition-colors">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gold/60 flex-shrink-0"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
                          Private Tours
                        </a>
                        <a href="/transfer-service" className="flex items-center gap-2.5 px-4 py-2.5 font-sans text-sm text-white/85 hover:text-gold hover:bg-white/5 transition-colors">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gold/60 flex-shrink-0"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                          Transfer Service
                        </a>
                        <a href="/10-year-saudi-visa-us-citizens" className="flex items-center gap-2.5 px-4 py-2.5 font-sans text-sm text-white/85 hover:text-gold hover:bg-white/5 transition-colors">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gold/60 flex-shrink-0"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>
                          10-Year Saudi Visa
                        </a>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="font-sans text-sm text-white hover:text-gold transition-colors"
                >
                  {link.label}
                </button>
              );
            })}
            {/* Language Toggle */}
            <button
              onClick={() => setLang(l => l === 'en' ? 'ru' : 'en')}
              className="rounded-full ring-2 ring-white/30 hover:ring-gold transition-all duration-300 hover:scale-110 shadow-md overflow-hidden"
              title={lang === 'en' ? 'Переключить на русский' : 'Switch to English'}
            >
              {lang === 'en' ? <RussiaFlag size={36} /> : <UKFlag size={36} />}
            </button>
            <a
              href="https://wa.me/79295627471"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold text-sm"
            >
              <Phone size={16} />
              <span>{t.nav.contact}</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-3">
            {/* Mobile Language Toggle */}
            <button
              onClick={() => setLang(l => l === 'en' ? 'ru' : 'en')}
              className="rounded-full ring-2 ring-white/30 hover:ring-gold transition-all duration-300 hover:scale-110 shadow-md overflow-hidden"
              title={lang === 'en' ? 'Переключить на русский' : 'Switch to English'}
            >
              {lang === 'en' ? <RussiaFlag size={32} /> : <UKFlag size={32} />}
            </button>
            <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 text-white"
          >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-parchment pt-32 px-6 md:hidden">
          <div className="flex flex-col gap-6">
            {navLinks.map((link) => {
              if (link.id === 'services') {
                return (
                  <div key={link.id}>
                    <button
                      onClick={() => setMobileServicesOpen(o => !o)}
                      className="font-sans text-2xl text-text-dark hover:text-gold transition-colors text-left flex items-center gap-2 w-full"
                    >
                      {link.label}
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}><path d="M6 9l6 6 6-6"/></svg>
                    </button>
                    {mobileServicesOpen && (
                      <div className="mt-3 ml-2 flex flex-col gap-1 border-l-2 border-gold/30 pl-4">
                        <a href="/umrah-guide" className="font-sans text-lg text-text-medium hover:text-gold transition-colors py-1">Umrah Guide</a>
                        <a href="/makkah-madinah-private-tours" className="font-sans text-lg text-text-medium hover:text-gold transition-colors py-1">Individual Tour</a>
                        <a href="/transfer-service" className="font-sans text-lg text-text-medium hover:text-gold transition-colors py-1">Transfer Service</a>
                        <a href="/10-year-saudi-visa-us-citizens" className="font-sans text-lg text-text-medium hover:text-gold transition-colors py-1">10-Year Saudi Visa</a>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="font-sans text-2xl text-text-dark hover:text-gold transition-colors text-left"
                >
                  {link.label}
                </button>
              );
            })}
            <a
              href="https://wa.me/79295627471"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold mt-4 justify-center"
            >
              <Phone size={18} />
              <span>{t.nav.contactUs}</span>
            </a>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center">
        {/* Background: static image on mobile, video on desktop */}
        <div className="absolute inset-0">
          <img
            src="/hero_main.webp"
            alt=""
            width="1344"
            height="768"
            className="block md:hidden w-full h-full object-cover"
            fetchPriority="high"
          />
          <video
            className="hidden md:block w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            poster="/hero_main.webp"
          >
            <source src="/hero_sec_vid.mp4" type="video/mp4" />
          </video>
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full px-6 sm:px-10 lg:px-16 pt-28 sm:pt-36 pb-8 sm:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-10 items-center">
            {/* Left: Heading & Sub-heading */}
            <div className="lg:pr-8 pt-4 lg:translate-y-[-30%]">
              <p className="text-white font-condensed text-sm sm:text-base tracking-[0.3em] mb-4 opacity-80">
                {t.hero.tagline}
              </p>
              <h1 className="font-serif text-[3rem] font-bold text-white leading-tight mb-6" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8), 0 1px 4px rgba(0,0,0,0.9)' }}>
                {t.hero.heading}
              </h1>
              <p className="font-sans text-xl sm:text-2xl font-semibold text-white/90 mb-5 sm:mb-10 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif', textShadow: '0 1px 8px rgba(0,0,0,0.8)' }}>
                {t.hero.subheading}
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <button
                  onClick={() => scrollToSection('services')}
                  className="w-full sm:w-64 inline-flex items-center justify-between gap-3 px-7 py-4 mt-3 mb-2 sm:mt-0 sm:mb-0 text-white text-sm font-sans font-semibold tracking-wide rounded-sm transition-all hover:brightness-110 hover:shadow-lg group"
                  style={{ backgroundColor: '#c9a86a' }}
                >
                  <span>{t.hero.exploreServices}</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-1"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </button>
              </div>
            </div>

            {/* Right: Video above, Form below, stacked vertically */}
            <div className="flex flex-col gap-3">

              {/* Video Card */}
              <div className="rounded-2xl overflow-hidden border-2 border-text-dark" style={{ aspectRatio: '16/9' }}>
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/vNgWnTs7J68?autoplay=0&rel=0"
                  title="Kingdom Group"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>

              {/* Form Card */}
              <div className="rounded-2xl border-2 border-text-dark p-5" style={{ backgroundColor: 'var(--parchment)' }}>
                <div className="mb-3">
                  <h3 className="font-serif text-xl text-text-dark">Drop us a line</h3>
                  <p className="font-condensed text-[10px] tracking-[0.22em] text-text-medium mt-0.5">// WE REPLY WITHIN 24H</p>
                </div>
                {heroFormState === 'success' ? (
                  <div className="py-6 text-center">
                    <p className="font-serif text-sm text-text-dark">Sent! We'll reply soon.</p>
                    <button
                      onClick={() => { setHeroFormState('idle'); setHeroFormData({ name: '', email: '', phone: '', message: '' }); }}
                      className="mt-2 font-sans text-xs text-gold underline"
                    >
                      Send another
                    </button>
                  </div>
                ) : (
                  <form
                    className="space-y-2.5"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      setHeroFormState('sending');
                      try {
                        const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? '';
                        const res = await fetch('/contact', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
                          body: JSON.stringify({ ...heroFormData, service: '', source: 'hero' }),
                        });
                        setHeroFormState(res.ok ? 'success' : 'error');
                      } catch {
                        setHeroFormState('error');
                      }
                    }}
                  >
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block font-condensed text-[10px] tracking-widest text-text-medium mb-1">NAME</label>
                        <input
                          type="text"
                          placeholder="Jordan Rivera"
                          required
                          value={heroFormData.name}
                          onChange={e => setHeroFormData(f => ({ ...f, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-border rounded-sm bg-white text-text-dark text-sm placeholder:text-text-light focus:outline-none focus:border-gold transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block font-condensed text-[10px] tracking-widest text-text-medium mb-1">EMAIL</label>
                        <input
                          type="email"
                          placeholder="you@domain.co"
                          value={heroFormData.email}
                          onChange={e => setHeroFormData(f => ({ ...f, email: e.target.value }))}
                          className="w-full px-3 py-2 border border-border rounded-sm bg-white text-text-dark text-sm placeholder:text-text-light focus:outline-none focus:border-gold transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block font-condensed text-[10px] tracking-widest text-text-medium mb-1">PHONE (OPTIONAL)</label>
                      <input
                        type="tel"
                        placeholder="+7 · · · · ·"
                        value={heroFormData.phone}
                        onChange={e => setHeroFormData(f => ({ ...f, phone: e.target.value }))}
                        className="w-full px-3 py-2 border border-border rounded-sm bg-white text-text-dark text-sm placeholder:text-text-light focus:outline-none focus:border-gold transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block font-condensed text-[10px] tracking-widest text-text-medium mb-1">MESSAGE</label>
                      <textarea
                        placeholder="What's on your mind?"
                        rows={2}
                        value={heroFormData.message}
                        onChange={e => setHeroFormData(f => ({ ...f, message: e.target.value }))}
                        className="w-full px-3 py-2 border border-border rounded-sm bg-white text-text-dark text-sm placeholder:text-text-light focus:outline-none focus:border-gold transition-colors resize-none"
                      />
                    </div>
                    {heroFormState === 'error' && (
                      <p className="font-sans text-xs text-red-500">Something went wrong. Please try again.</p>
                    )}
                    <div className="flex items-center justify-between pt-0.5">
                      <span className="font-condensed text-[10px] tracking-widest text-text-medium">↵ enter to send</span>
                      <button
                        type="submit"
                        disabled={heroFormState === 'sending'}
                        className="font-condensed text-[11px] tracking-widest px-5 py-2.5 rounded-full disabled:opacity-60 transition-opacity"
                        style={{ backgroundColor: 'var(--text-dark)', color: 'var(--parchment)' }}
                      >
                        {heroFormState === 'sending' ? '...' : 'SEND IT'}
                      </button>
                    </div>
                  </form>
                )}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section id="about" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="reveal">
              <p className="text-label mb-4">{t.about.label}</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-text-dark mb-6">
                {t.about.heading}
              </h2>
              <div className="w-16 h-0.5 bg-gold mb-6" />
              <p className="font-sans text-text-medium leading-relaxed mb-6">
                {t.about.p1}
              </p>
              <p className="font-sans text-text-medium leading-relaxed">
                {t.about.p2}
              </p>
            </div>
            <div className="relative reveal">
              <img
                src="/hero_masjid_nabawi_interior.webp"
                alt="Masjid an-Nabawi interior"
                width="1344"
                height="768"
                className="w-full h-80 sm:h-96 object-cover rounded-sm shadow-lg"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 reveal">
            <p className="text-label mb-4">{t.services.label}</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-text-dark mb-4">
              {t.services.heading}
            </h2>
            <div className="w-16 h-0.5 bg-gold mx-auto" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 reveal-stagger">
            {/* Service 1 */}
            <a href="/umrah-guide" className="service-card cursor-pointer hover:shadow-lg transition-shadow duration-200" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="w-14 h-14 bg-gold/10 rounded-full flex items-center justify-center mb-6">
                <MapPin className="text-gold" size={28} />
              </div>
              <h3 className="font-serif text-xl text-text-dark mb-3">{t.services.s1Title}</h3>
              <p className="font-sans text-text-medium text-sm leading-relaxed mb-4">{t.services.s1Desc}</p>
              <span className="mt-auto self-start font-condensed text-sm tracking-wider bg-gold text-white rounded-full px-4 py-1.5 shadow-sm">{t.services.s1Price}</span>
              <span className="mt-3 self-start font-sans text-xs text-gold tracking-wide flex items-center gap-1">Learn More <ArrowRight size={11} /></span>
            </a>

            {/* Service 2 */}
            <a href="/makkah-madinah-private-tours" className="service-card cursor-pointer hover:shadow-lg transition-shadow duration-200" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="w-14 h-14 bg-gold/10 rounded-full flex items-center justify-center mb-6">
                <Users className="text-gold" size={28} />
              </div>
              <h3 className="font-serif text-xl text-text-dark mb-3">{t.services.s2Title}</h3>
              <p className="font-sans text-text-medium text-sm leading-relaxed mb-4">{t.services.s2Desc}</p>
              <span className="mt-auto self-start font-condensed text-sm tracking-wider bg-gold text-white rounded-full px-4 py-1.5 shadow-sm">{t.services.s2Price}</span>
              <span className="mt-3 self-start font-sans text-xs text-gold tracking-wide flex items-center gap-1">Learn More <ArrowRight size={11} /></span>
            </a>

            {/* Service 3 — Transfer Service */}
            <a href="/transfer-service" className="service-card cursor-pointer hover:shadow-lg transition-shadow duration-200" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="w-14 h-14 bg-gold/10 rounded-full flex items-center justify-center mb-6">
                <Building2 className="text-gold" size={28} />
              </div>
              <h3 className="font-serif text-xl text-text-dark mb-3">{t.services.s4Title}</h3>
              <p className="font-sans text-text-medium text-sm leading-relaxed mb-4">{t.services.s4Desc}</p>
              <span className="mt-auto self-start font-condensed text-sm tracking-wider bg-gold text-white rounded-full px-4 py-1.5 shadow-sm">{t.services.s4Price}</span>
              <span className="mt-3 self-start font-sans text-xs text-gold tracking-wide flex items-center gap-1">Learn More <ArrowRight size={11} /></span>
            </a>

            {/* Service 4 */}
            <a href="/10-year-saudi-visa-us-citizens" className="service-card cursor-pointer hover:shadow-lg transition-shadow duration-200" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="w-14 h-14 bg-gold/10 rounded-full flex items-center justify-center mb-6">
                <Globe className="text-gold" size={28} />
              </div>
              <h3 className="font-serif text-xl text-text-dark mb-3">{t.services.s5Title}</h3>
              <p className="font-sans text-text-medium text-sm leading-relaxed mb-4">{t.services.s5Desc}</p>
              <span className="mt-auto self-start font-condensed text-sm tracking-wider bg-gold text-white rounded-full px-4 py-1.5 shadow-sm">{t.services.s5Price}</span>
              <span className="mt-3 self-start font-sans text-xs text-gold tracking-wide flex items-center gap-1">Learn More <ArrowRight size={11} /></span>
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 reveal">
            <p className="text-label mb-4">{t.why.label}</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-text-dark mb-4">
              {t.why.heading}
            </h2>
            <div className="w-16 h-0.5 bg-gold mx-auto" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 reveal-stagger">
            {t.why.items.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-3 h-3 bg-gold rotate-45 mx-auto mb-4" />
                <h3 className="font-serif text-lg text-text-dark mb-2">{item.title}</h3>
                <p className="font-sans text-text-medium text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Destinations Gallery */}
      <section id="destinations" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 reveal">
            <p className="text-label mb-4">{t.destinations.label}</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-text-dark mb-4">
              {t.destinations.heading}
            </h2>
            <div className="w-16 h-0.5 bg-gold mx-auto" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 reveal-stagger">
            {[
              { src: '/dest_mecca_night.webp', w: 864, h: 1184 },
              { src: '/dest_madina_exterior.webp', w: 864, h: 1184 },
              { src: '/dest_alula_rocks.webp', w: 864, h: 1184 },
              { src: '/cities_riyadh_tower.webp', w: 1344, h: 768 },
              { src: '/cities_jeddah_waterfront.webp', w: 1344, h: 768 },
              { src: '/exp_redsea_coast.webp', w: 1344, h: 768 },
            ].map(({ src, w, h }, index) => (
              <div key={index} className="destination-card group">
                <img
                  src={src}
                  alt={t.destinations.places[index]}
                  width={w}
                  height={h}
                  className="w-full h-48 sm:h-64 object-cover"
                  loading="lazy"
                />
                <div className="destination-overlay">
                  <p className="font-sans text-base font-medium tracking-wide text-white">{t.destinations.places[index]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section id="reviews" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12 bg-white/50 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 reveal">
            <p className="text-label mb-4">{t.reviews.label}</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-text-dark mb-4">
              {t.reviews.heading}
            </h2>
            <div className="w-16 h-0.5 bg-gold mx-auto mb-6" />
            {/* Google Rating Badge */}
            <a
              href="https://www.google.com/maps/place/Kingdom+Group/@24.4675,39.6136,17z"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white border border-border rounded-full px-5 py-2.5 shadow-sm hover:shadow-md transition-shadow"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="font-condensed text-text-dark text-sm tracking-wide">5.0</span>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#C9A86A">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
              </div>
              <span className="font-sans text-text-medium text-xs">{t.reviews.googleCount}</span>
            </a>
          </div>

          {(() => {
            const reviews = t.reviews.items;

            const renderCard = (review: typeof reviews[0], i: number) => (
              <div key={i} className="bg-white rounded-sm p-5 shadow-sm border border-border/60 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                      <span className="font-condensed text-gold text-sm font-bold">{review.name[0]}</span>
                    </div>
                    <div>
                      <p className="font-sans text-sm font-semibold text-text-dark leading-tight">{review.name}</p>
                      <p className="font-sans text-xs text-text-light">{review.time}</p>
                    </div>
                  </div>
                  <svg viewBox="0 0 24 24" width="16" height="16" className="flex-shrink-0 mt-0.5">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                </div>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, s) => (
                    <svg key={s} width="13" height="13" viewBox="0 0 24 24" fill="#C9A86A">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>
                <p className="font-sans text-sm text-text-medium leading-relaxed flex-1">{review.text}</p>
              </div>
            );

            return (
              <>
                {/* First 4, always visible */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 reveal-stagger">
                  {reviews.slice(0, 4).map((r, i) => renderCard(r, i))}
                </div>

                {/* Last 4, slide in/out on mobile, always visible on sm+ */}
                {/* Outer: grid-rows trick for smooth height animation */}
                <div
                  className="grid transition-[grid-template-rows] duration-500 ease-in-out sm:grid-rows-[1fr]"
                  style={{ gridTemplateRows: showAllReviews ? '1fr' : '0fr' }}
                >
                  <div className="overflow-hidden min-h-0">
                    <div
                      className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 transition-opacity duration-500 ease-in-out ${showAllReviews ? 'opacity-100' : 'opacity-0 sm:opacity-100'}`}
                    >
                      {reviews.slice(4).map((r, i) => renderCard(r, i + 4))}
                    </div>
                  </div>
                </div>

                {/* Mobile-only toggle button */}
                <div className="flex justify-center mt-6 sm:hidden">
                  <button
                    onClick={() => setShowAllReviews(!showAllReviews)}
                    className="inline-flex items-center gap-2 px-6 py-2.5 border border-gold text-gold font-condensed text-sm tracking-wider rounded-sm hover:bg-gold hover:text-white transition-colors duration-300"
                  >
                    {showAllReviews ? t.reviews.showLess : t.reviews.showAll}
                    <svg
                      width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      className={`transition-transform duration-500 ease-in-out ${showAllReviews ? 'rotate-180' : ''}`}
                    >
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 sm:py-24 px-6 sm:px-10 lg:px-16 bg-midnight">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 reveal">
            <p className="font-condensed text-sm tracking-[0.3em] text-gold mb-3">{t.testimonials.label}</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-white font-bold">{t.testimonials.heading}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 reveal-stagger">
            {[
              '7530555189430947080',
              '7524288163477671175',
              '7580989871158824199',
              '7510580484502473991',
            ].map((id) => (
              <TikTokCard key={id} id={id} username="jamshedsanginov_" onPlay={() => setActiveTikTok(id)} watchLabel={t.tiktok.watch} />
            ))}
          </div>
        </div>
      </section>

      {/* TikTok Section */}
      <section id="tiktok" className="py-16 sm:py-24 px-6 sm:px-10 lg:px-16 bg-midnight border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 reveal">
            <p className="font-condensed text-sm tracking-[0.3em] text-gold mb-3">{t.tiktok.label}</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-white font-bold">{t.tiktok.heading}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 reveal-stagger">
            {[
              '7569981012185189650',
              '7558401588482592008',
              '7436326882003504401',
              '7620691692802395412',
            ].map((id) => (
              <TikTokCard key={id} id={id} username="jamshedsanginov_" onPlay={() => setActiveTikTok(id)} watchLabel={t.tiktok.watch} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12 bg-midnight">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <p className="text-label mb-4">{t.contact.label}</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-white mb-6">
                {t.contact.heading}
              </h2>
              <p className="font-sans text-text-light mb-8">
                {t.contact.subtext}
              </p>

              <div className="space-y-6">
                <a 
                  href="tel:+966570385779"
                  className="flex items-center gap-4 text-white hover:text-gold transition-colors"
                >
                  <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center">
                    <Phone size={20} className="text-gold" />
                  </div>
                  <div>
                    <p className="font-sans text-sm text-text-light">{t.contact.phone}</p>
                    <p className="font-sans">+966 570 385 779</p>
                  </div>
                </a>

                <a
                  href="https://wa.me/79295627471"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 text-white hover:text-gold transition-colors"
                >
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                    <WhatsAppIcon size={20} className="text-green-500" />
                  </div>
                  <div>
                    <p className="font-sans text-sm text-text-light">{t.contact.whatsapp}</p>
                    <p className="font-sans">+7 929 562 7471</p>
                  </div>
                </a>

                <a
                  href="https://t.me/+79295627471"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 text-white hover:text-gold transition-colors"
                >
                  <div className="w-12 h-12 bg-sky-500/20 rounded-full flex items-center justify-center">
                    <TelegramIcon size={20} className="text-sky-400" />
                  </div>
                  <div>
                    <p className="font-sans text-sm text-text-light">{t.contact.telegram}</p>
                    <p className="font-sans">+7 929 562 7471</p>
                  </div>
                </a>

                <a
                  href="https://www.instagram.com/kingdomgroup.sa?igsh=M2ZqcHpwZ2U5cHFn&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 text-white hover:text-gold transition-colors"
                >
                  <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center">
                    <Instagram size={20} className="text-gold" />
                  </div>
                  <div>
                    <p className="font-sans text-sm text-text-light">{t.contact.instagram}</p>
                    <p className="font-sans">@kingdomgroup.sa</p>
                  </div>
                </a>

                <a
                  href="https://www.tiktok.com/@jamshedsanginov_?_r=1&_t=ZS-952dIuasJgD"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 text-white hover:text-gold transition-colors"
                >
                  <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center">
                    <svg className="text-gold" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-sans text-sm text-text-light">{t.contact.tiktok}</p>
                    <p className="font-sans">@jamshedsanginov_</p>
                  </div>
                </a>

                <a 
                  href="mailto:hello@kingdomgroup.sa"
                  className="flex items-center gap-4 text-white hover:text-gold transition-colors"
                >
                  <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center">
                    <Send size={20} className="text-gold" />
                  </div>
                  <div>
                    <p className="font-sans text-sm text-text-light">{t.contact.email}</p>
                    <p className="font-sans">hello@kingdomgroup.sa</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-parchment rounded-sm p-6 sm:p-8">
              <h3 className="font-serif text-2xl text-text-dark mb-6">{t.contact.formTitle}</h3>

              {formState === 'success' ? (
                <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
                  <div className="w-14 h-14 rounded-full bg-gold/20 flex items-center justify-center">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C9A86A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                  <p className="font-serif text-xl text-text-dark">{t.contact.successTitle}</p>
                  <p className="font-sans text-sm text-text-medium">{t.contact.successSubtitle}</p>
                  <button
                    onClick={() => { setFormState('idle'); setFormData({ name: '', email: '', phone: '', service: '', message: '' }); }}
                    className="mt-2 font-sans text-sm text-gold underline underline-offset-2 hover:text-gold-dark transition-colors"
                  >
                    {t.contact.sendAnother}
                  </button>
                </div>
              ) : (
                <form
                  className="space-y-4"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setFormState('sending');
                    try {
                      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? '';
                      const res = await fetch('/contact', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
                        body: JSON.stringify(formData),
                      });
                      setFormState(res.ok ? 'success' : 'error');
                    } catch {
                      setFormState('error');
                    }
                  }}
                >
                  <div>
                    <label className="block font-sans text-sm text-text-medium mb-2">{t.contact.nameLabel}</label>
                    <input
                      type="text"
                      placeholder={t.contact.namePlaceholder}
                      required
                      value={formData.name}
                      onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-border rounded-sm bg-white text-text-dark placeholder:text-text-light focus:outline-none focus:border-gold transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-sm text-text-medium mb-2">{t.contact.emailLabel}</label>
                    <input
                      type="email"
                      placeholder={t.contact.emailPlaceholder}
                      value={formData.email}
                      onChange={e => setFormData(f => ({ ...f, email: e.target.value }))}
                      className="w-full px-4 py-3 border border-border rounded-sm bg-white text-text-dark placeholder:text-text-light focus:outline-none focus:border-gold transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-sm text-text-medium mb-2">{t.contact.phoneLabel}</label>
                    <input
                      type="tel"
                      placeholder={t.contact.phonePlaceholder}
                      value={formData.phone}
                      onChange={e => setFormData(f => ({ ...f, phone: e.target.value }))}
                      className="w-full px-4 py-3 border border-border rounded-sm bg-white text-text-dark placeholder:text-text-light focus:outline-none focus:border-gold transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-sm text-text-medium mb-2">{t.contact.serviceLabel}</label>
                    <select
                      value={formData.service}
                      onChange={e => setFormData(f => ({ ...f, service: e.target.value }))}
                      className="w-full px-4 py-3 border border-border rounded-sm bg-white text-text-dark focus:outline-none focus:border-gold transition-colors"
                    >
                      <option value="">{t.contact.servicePlaceholder}</option>
                      {t.contact.serviceOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block font-sans text-sm text-text-medium mb-2">{t.contact.messageLabel}</label>
                    <textarea
                      placeholder={t.contact.messagePlaceholder}
                      rows={4}
                      value={formData.message}
                      onChange={e => setFormData(f => ({ ...f, message: e.target.value }))}
                      className="w-full px-4 py-3 border border-border rounded-sm bg-white text-text-dark placeholder:text-text-light focus:outline-none focus:border-gold transition-colors resize-none"
                    />
                  </div>
                  {formState === 'error' && (
                    <p className="font-sans text-sm text-red-500">{t.contact.errorMsg}</p>
                  )}
                  <button type="submit" disabled={formState === 'sending'} className="btn-gold w-full justify-center disabled:opacity-60">
                    {formState === 'sending' ? (
                      <>
                        <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                          <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                        </svg>
                        <span>{t.contact.sending}</span>
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        <span>{t.contact.submit}</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-midnight border-t border-white/10 py-8 px-4 sm:px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <img
                src="/logo.webp"
                alt="Kingdom Group"
                width="957"
                height="522"
                className="h-10 w-auto object-contain"
                loading="lazy"
              />
              <div>
                <p className="font-serif text-lg text-white">Kingdom Group</p>
                <p className="font-sans text-sm text-text-light">{t.footer.tagline}</p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <a
                href="https://www.tiktok.com/@jamshedsanginov_?_r=1&_t=ZS-952dIuasJgD"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-light hover:text-gold transition-colors"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/kingdomgroup.sa?igsh=M2ZqcHpwZ2U5cHFn&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-light hover:text-gold transition-colors"
              >
                <Instagram size={22} />
              </a>
              <a
                href="https://wa.me/79295627471"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-light hover:text-gold transition-colors"
              >
                <WhatsAppIcon size={22} />
              </a>
            </div>

            <p className="font-sans text-sm text-text-light">
              {t.footer.copyright}
            </p>
          </div>
        </div>
      </footer>

      {/* TikTok Modal */}
      {activeTikTok && (
        <TikTokModal
          id={activeTikTok}
          onClose={() => setActiveTikTok(null)}
        />
      )}

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/79295627471?text=Hello!%20I'm%20interested%20in%20learning%20more%20about%20your%20services."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
        aria-label="Contact us on WhatsApp"
      >
        <WhatsAppIcon size={28} className="text-white" />
      </a>
    </div>
  );
}

export default App;
